import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import useCreateAgendaEvent from "@/hooks/agenda/useCreateAgendaEvent";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { createAgendaItemDataToAgendaItemMapper } from "@/mappers/agenda/agendaItemMapper";
import routes from "@/router/routes";
import { closeAssistant } from "@/stores/assistantStore";
import { focusEventInCalendar } from "@/stores/eventStore";
import {
    CalendarDays,
    Check,
    House,
    Loader2,
    PenLine,
    User,
    type LucideIcon,
} from "lucide-react";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EventEditForm from "../widgets/dashboard/calendar/eventModal/form/EventEditForm";
import {
    pillLavenderButtonClassName,
    pillPrimaryButtonClassName,
} from "./assistantClasses";
import { eventTimeRange } from "./EventDetailsBlock";
import eventProposalToCreateData from "./eventProposal";
import formatDuration from "./formatDuration";

type CreateAgendaItemResponseProps = {
    eventData: CreateAgendaItemData;
};

const iconClassName = "mt-0.5 size-5 flex-none text-primary-dark";

const textOf = (html: string) =>
    new DOMParser()
        .parseFromString(
            html.replace(/<br\s*\/?>|<\/(p|div|li)>/gi, "$&\n"),
            "text/html",
        )
        .body.textContent?.trim() ?? "";

const Row = ({
    Icon,
    title,
    children,
}: {
    Icon: LucideIcon;
    title: ReactNode;
    children?: ReactNode;
}) => (
    <div className="flex items-start gap-3">
        <Icon aria-hidden className={iconClassName} strokeWidth={1.5} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="break-words text-base font-semibold text-primary-darkest first-letter:uppercase">
                {title}
            </p>
            {children}
        </div>
    </div>
);

/**
 * The event the assistant proposes, as a card like the rest of the chat: a draft with its title, when,
 * who is invited, where and the agenda. « Créer l'événement » creates it as proposed; « Modifier » opens
 * the calendar's form in its light look (the same form as the dashboard's, on a white card) to change it
 * first. Created, the card stays, read-only, with « Voir dans l'agenda ».
 */
const CreateAgendaItemResponse = ({
    eventData,
}: CreateAgendaItemResponseProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { data: user } = useGetUserProfile();
    const [state, setState] = useState<"proposed" | "editing" | "created">(
        "proposed",
    );
    const [created, setCreated] = useState<CreateAgendaItemData>();
    const shown = created ?? eventData;
    const event = useMemo(
        () => createAgendaItemDataToAgendaItemMapper(shown, user),
        [shown, user],
    );
    const { createAgendaEvent } = useCreateAgendaEvent(() =>
        setState("created"),
    );
    const attendees = (shown.attendees ?? []).filter(
        (attendee) => attendee !== user?.profile?.email,
    );
    const description = created
        ? textOf(created.description ?? "")
        : eventData.description;
    const isCreated = state === "created";
    const when = [
        event.start.toLocaleDateString(i18n.language, {
            weekday: "long",
            day: "numeric",
            month: "long",
        }),
        eventTimeRange(
            event,
            i18n.language,
            t("dashboard.widgets.calendar.event.allDay"),
        ),
    ].join(" · ");
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const openInCalendar = () => {
        closeAssistant();
        focusEventInCalendar(event);
        navigate(routes.dashboard);
    };

    if (state === "editing" && user) {
        return (
            <section
                aria-label={t("vistassistant.createAgendaItemResponse.label")}
                className="flex w-full max-w-[720px] flex-col gap-4 rounded-[20px] border border-assistant-highlight bg-white p-5"
            >
                <div className="flex flex-col gap-2">
                    <h3 className="flex items-center gap-2.5 text-2xl font-semibold text-primary-darkest">
                        <PenLine
                            aria-hidden
                            className="size-5 flex-none text-primary-dark"
                            strokeWidth={1.5}
                        />
                        {t("vistassistant.createAgendaItemResponse.editTitle")}
                    </h3>
                    <p className="text-base text-assistant-muted">
                        {t("vistassistant.createAgendaItemResponse.editHint")}
                    </p>
                </div>
                <EventEditForm
                    event={event}
                    onCancel={() => setState("proposed")}
                    onSuccess={(data) => {
                        if (data) setCreated(data);
                        setState("created");
                    }}
                    submitLabel={t(
                        "vistassistant.createAgendaItemResponse.create",
                    )}
                    tone="light"
                />
            </section>
        );
    }

    return (
        <section
            aria-label={t("vistassistant.createAgendaItemResponse.label")}
            className={cn(
                "flex w-full max-w-[720px] flex-col gap-4 rounded-[20px] border bg-white p-5",
                isCreated
                    ? "border-assistant-cardBorder"
                    : "border-assistant-highlight",
            )}
        >
            {isCreated ? (
                <span className="flex w-fit items-center gap-2 rounded-full bg-assistant-doneBg px-3 py-1 text-[15px] font-semibold text-assistant-done">
                    <Check aria-hidden className="size-4" strokeWidth={2} />
                    {t("vistassistant.createAgendaItemResponse.created")}
                </span>
            ) : (
                <span className="flex w-fit items-center gap-2 px-2 py-1 text-[15px] font-semibold text-assistant-ink">
                    <PenLine
                        aria-hidden
                        className="size-4"
                        strokeWidth={1.75}
                    />
                    {t("vistassistant.createAgendaItemResponse.draft")}
                </span>
            )}
            <h3 className="break-words text-[26px] font-bold leading-tight text-primary-darkest">
                {shown.subject}
            </h3>
            <div className="flex flex-col gap-4">
                <Row Icon={CalendarDays} title={when}>
                    {!event.isAllDay && (
                        <p className="text-[15px] text-assistant-muted">
                            {formatDuration(event.start, event.end, t)} ·{" "}
                            {t("vistassistant.createAgendaItemResponse.zone", {
                                zone,
                            })}
                        </p>
                    )}
                </Row>
                {attendees.length > 0 && (
                    <Row
                        Icon={User}
                        title={t(
                            "dashboard.widgets.calendar.event.attendeesCount",
                            { count: attendees.length },
                        )}
                    >
                        <ul className="flex min-w-0 flex-wrap gap-2 pt-0.5">
                            {attendees.map((attendee) => (
                                <li
                                    key={attendee}
                                    className="max-w-full truncate rounded-full bg-assistant-lavender px-3 py-1 text-[15px] font-semibold text-primary-darkest"
                                >
                                    {attendee}
                                </li>
                            ))}
                        </ul>
                    </Row>
                )}
                {shown.location && <Row Icon={House} title={shown.location} />}
                {description && (
                    <Row
                        Icon={PenLine}
                        title={t(
                            "vistassistant.createAgendaItemResponse.agenda",
                        )}
                    >
                        <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-assistant-muted">
                            {description}
                        </p>
                    </Row>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {isCreated ? (
                    <button
                        className={pillPrimaryButtonClassName}
                        onClick={openInCalendar}
                        type="button"
                    >
                        <CalendarDays aria-hidden strokeWidth={1.75} />
                        {t(
                            "vistassistant.createAgendaItemResponse.openInCalendar",
                        )}
                    </button>
                ) : (
                    <>
                        <button
                            className={pillLavenderButtonClassName}
                            disabled={!user || createAgendaEvent.isPending}
                            onClick={() => setState("editing")}
                            type="button"
                        >
                            <PenLine aria-hidden strokeWidth={1.75} />
                            {t("vistassistant.createAgendaItemResponse.edit")}
                        </button>
                        <button
                            className={pillPrimaryButtonClassName}
                            disabled={createAgendaEvent.isPending}
                            onClick={() =>
                                createAgendaEvent.mutate({
                                    createAgendaItemData:
                                        eventProposalToCreateData(event),
                                })
                            }
                            type="button"
                        >
                            {createAgendaEvent.isPending ? (
                                <Loader2 aria-hidden className="animate-spin" />
                            ) : (
                                <CalendarDays aria-hidden strokeWidth={1.75} />
                            )}
                            {t("vistassistant.createAgendaItemResponse.create")}
                        </button>
                    </>
                )}
            </div>
        </section>
    );
};

export default CreateAgendaItemResponse;
